/**
 * マイナス検索文字列（除外検索クエリ）の生成ロジック。
 * 検索結果画面（AiOcrResult）と CSV/Excel 出力（AiOcr）で共通利用する。
 */

/**
 * 勤務先候補テキストから「【勤務先候補N】」行の会社名のみを抽出する。
 * 「その他の活動情報」セクションは勤務先ではないため対象に含めない。
 */
const extractCompanyNames = (text: string): string[] => {
  // 「その他の活動情報」以降は勤務先候補ではないので切り捨てる
  const companySection = text.split('【その他の活動情報】')[0] ?? '';
  return companySection
    .split('\n')
    .filter(line => /【勤務先候補[0-9０-９]+】/.test(line))
    .map(line => line.replace(/【勤務先候補[0-9０-９]+】/, '').trim())
    .filter(Boolean);
};

/**
 * 会社名を法人名の単位に正規化する。
 * 例: 「東京慈恵会医科大学 小児科学講座」→「東京慈恵会医科大学」
 *     スペース区切りの後半に付く講座名・部署名・診療科を除去する。
 */
const normalizeCompanyName = (company: string): string => {
  // 全角・半角スペースで分割
  const parts = company.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return company.trim();

  // 法人格・組織名を表す末尾語。ここまでを法人名とみなす。
  const orgSuffixWords = [
    '株式会社',
    '有限会社',
    '合同会社',
    '合資会社',
    '合名会社',
    '医療法人',
    '社会福祉法人',
    '学校法人',
    '一般社団法人',
    '公益社団法人',
    '一般財団法人',
    '公益財団法人',
    '独立行政法人',
    '国立大学法人',
    '大学院',
    '大学',
    '高等専門学校',
    '高等学校',
    '高校',
    '中学校',
    '小学校',
    '病院',
    '医院',
    '診療所',
    'クリニック',
    'センター',
    '機構',
    '協会',
    '組合',
    '公社',
    '財団',
    '社団',
    '研究所',
    '研究センター',
    '事務所',
    '銀行',
    '信用金庫',
    '信用組合',
    '農業協同組合',
    '生活協同組合',
  ];
  const orgSuffix = new RegExp(`(${orgSuffixWords.join('|')})$`);

  // 法人格語を含むトークンまでで打ち切る（それ以降の講座・部署・診療科は除外）
  for (let i = 0; i < parts.length; i += 1) {
    if (orgSuffix.test(parts[i])) {
      return parts.slice(0, i + 1).join('');
    }
  }
  // 法人格語が見つからない場合は、安全側に倒して全語をそのまま採用する。
  // （先頭1語に削ると「三菱 UFJ銀行」→「三菱」のように除外範囲が広がりすぎ、
  //   本来探したい情報まで検索から消えてしまうため）
  return parts.join('');
};

/**
 * 住所文字列から都道府県名を抽出する。
 */
const extractPrefecture = (address: string): string =>
  address.match(/^(.{2,3}[都道府県])/)?.[1] ?? '';

/**
 * 勤務先候補テキスト（Gemini / Perplexity）と債務者氏名・住所から
 * マイナス検索文字列を生成する。
 *
 * - 勤務先候補のみを対象とし、法人名単位に正規化して除外語にする
 * - 空欄・空白は除去し、二重スペースが残らないように整形する
 * - 該当する会社が 1 件も無い場合は null を返す
 */
export const buildMinusSearchQuery = (params: {
  candidateCompaniesByGemini?: string | null;
  candidateCompanies?: string | null;
  debtorName?: string | null;
  debtorAddress?: string | null;
}): string | null => {
  const geminiCompanies = extractCompanyNames(params.candidateCompaniesByGemini ?? '');
  const perplexityCompanies = extractCompanyNames(params.candidateCompanies ?? '');

  const normalized = [...geminiCompanies, ...perplexityCompanies]
    .map(normalizeCompanyName)
    .filter(Boolean);

  // 重複排除（正規化後に重複しうるため）
  const allCompanies = normalized.filter((c, i) => normalized.indexOf(c) === i);
  if (allCompanies.length === 0) return null;

  const name = (params.debtorName ?? '').trim();
  const prefecture = extractPrefecture((params.debtorAddress ?? '').trim());
  const minusKeywords = allCompanies.map(c => `-${c}`).join(' ');

  // 空欄を含めないよう、各要素を組み立ててから空要素を除外・スペース正規化する
  return [name ? `"${name}"` : '', prefecture, minusKeywords]
    .filter(Boolean)
    .join(' ')
    .replace(/\s+/g, ' ')
    .trim();
};
